import { useContext, useEffect, useMemo, useState } from 'react';

import Message from '../../../../components/ui/Message';
import { LoyaltyContext, WalletImage } from '../../../../context/loyalty/LoyaltyContext';
import { WalletConfig, WalletConfigChanges, WalletModel, WalletProgram, WalletSaveResult } from '../../../../models/interfaces';
import { WalletImageField } from './WalletImageField';
import { WalletMyDesigns } from './WalletMyDesigns';
import {
    downloadBlob, GOOGLE_LOGO_MIN, HERO_GOOGLE_SIZE, isHexColor, PROGRAM_LABEL, STRIP_SIZE, TEMPLATE_URL, TEXT_LIMITS, toHexColor
} from './walletUtils';

type FileKey = 'logo' | 'icono' | 'logoGoogle' | 'strip' | 'heroGoogle';
type TextKey = 'contacto' | 'terminos' | 'premio';
type ColorKey = 'fondo' | 'texto' | 'etiqueta';

const IMAGE_ENDPOINT: Record<FileKey, WalletImage> = {
    logo: 'logo', icono: 'icono', logoGoogle: 'logo-google', strip: 'strip', heroGoogle: 'hero-google',
};
const TEXT_FIELD: Record<TextKey, 'textoContacto' | 'textoTerminos' | 'textoPremio'> = {
    contacto: 'textoContacto', terminos: 'textoTerminos', premio: 'textoPremio',
};
const COLOR_FIELD: Record<ColorKey, 'colorFondo' | 'colorTexto' | 'colorEtiqueta'> = {
    fondo: 'colorFondo', texto: 'colorTexto', etiqueta: 'colorEtiqueta',
};
const MISSING_LABEL: Record<string, string> = { logo: 'Logo (iPhone)', icono: 'Ícono (iPhone)' };

const emptyFiles: Record<FileKey, File | null> = { logo: null, icono: null, logoGoogle: null, strip: null, heroGoogle: null };

// Acciones que se guardan solas (no se pueden combinar con otros campos en el mismo envío)
type ResetAction = 'restablecerModelo' | 'limpiarTextos' | 'restablecerColores' | 'restablecerStrip' | 'restablecerHeroGoogle';
const RESET_CONFIRM: Record<ResetAction, string> = {
    restablecerModelo: 'La tarjeta volverá a usar el diseño predeterminado.',
    limpiarTextos: 'Se borrarán el contacto, los términos y el premio guardados. Las tarjetas dejarán de mostrarlos.',
    restablecerColores: 'Se quitarán los colores propios y la tarjeta usará los colores del diseño.',
    restablecerStrip: 'Se quitará el banner propio de iPhone y se usará el del diseño.',
    restablecerHeroGoogle: 'Se quitará el banner propio de Android y se usará el del diseño.',
};

interface WalletProgramPanelProps {
    tipo: WalletProgram;
    config: WalletConfig;
    onReload: () => Promise<void>;
}

// Configuración de la tarjeta digital de un programa (puntos o sellos): imágenes, diseño, textos y opciones avanzadas
export const WalletProgramPanel = ({ tipo, config, onReload }: WalletProgramPanelProps) => {
    const {
        loadWalletModels, getWalletModelBanner, getWalletImage, saveWalletConfig,
        previewWalletApple, previewWalletGoogle, syncWalletGoogle
    } = useContext(LoyaltyContext);

    const stamps = tipo === 'sellos';
    const effective = config.disenoEfectivo;
    const chosenModel = effective?.modeloElegidoPorComercio ? effective.modelo : null;
    const customStrip = effective?.banner === 'propio';
    const customHero = config.google?.banner === 'propio';

    // -------------------- Catálogo de diseños --------------------

    const [models, setModels] = useState<WalletModel[]>([]);
    const [modelsError, setModelsError] = useState('');
    const [loadingModels, setLoadingModels] = useState(true);
    const [banners, setBanners] = useState<Record<string, string>>({});
    const [modelsVersion, setModelsVersion] = useState(0);   // se incrementa al cambiar "Mis diseños"

    useEffect(() => {
        let cancelled = false;
        const urls: string[] = [];
        setBanners({});
        (async () => {
            const resp = await loadWalletModels(tipo);
            if (cancelled) return;
            setLoadingModels(false);
            if (resp.error) { setModelsError(resp.error); return; }
            setModelsError('');
            setModels(resp.models ?? []);
            // bannerUrl requiere el token: se descarga como Blob y se muestra con un object URL
            for (const model of resp.models ?? []) {
                getWalletModelBanner(model.codigo).then(blob => {
                    if (!blob || cancelled) return;
                    const url = URL.createObjectURL(blob);
                    urls.push(url);
                    setBanners(prev => ({ ...prev, [model.codigo]: url }));
                });
            }
        })();
        return () => { cancelled = true; urls.forEach(url => URL.revokeObjectURL(url)); };
    }, [tipo, modelsVersion]);

    // Después de crear, editar, eliminar o usar un diseño propio
    const reloadDesigns = async () => {
        setModelsVersion(v => v + 1);
        await onReload();
    };

    // -------------------- Imágenes guardadas --------------------

    const [savedImages, setSavedImages] = useState<Partial<Record<FileKey, string>>>({});
    const [imagesVersion, setImagesVersion] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const urls: string[] = [];
        const keys: FileKey[] = ['logo', 'icono', 'logoGoogle'];
        if (customStrip) keys.push('strip');
        if (customHero) keys.push('heroGoogle');

        Promise.all(keys.map(async key => {
            const blob = await getWalletImage(tipo, IMAGE_ENDPOINT[key]);  // null si no hay (404)
            if (!blob || cancelled) return [key, undefined] as const;
            const url = URL.createObjectURL(blob);
            urls.push(url);
            return [key, url] as const;
        })).then(entries => {
            if (cancelled) return;
            setSavedImages(Object.fromEntries(entries.filter(([, url]) => url)));
        });
        return () => { cancelled = true; urls.forEach(url => URL.revokeObjectURL(url)); };
    }, [tipo, imagesVersion, customStrip, customHero]);

    // -------------------- Formulario --------------------

    // Las dos configuraciones se recargan juntas: se compara por contenido para no perder
    // los cambios pendientes del otro programa cuando se guarda uno
    const configKey = JSON.stringify(config);

    const savedTexts = useMemo(() => ({
        contacto: config.textos?.contacto ?? '',
        terminos: config.textos?.terminos ?? '',
        premio: config.textos?.premio ?? '',
    }), [configKey]);
    const savedColors = useMemo(() => ({
        fondo: toHexColor(config.coloresPropios?.fondo),
        texto: toHexColor(config.coloresPropios?.texto),
        etiqueta: toHexColor(config.coloresPropios?.etiqueta),
    }), [configKey]);
    const hasCustomColors = Object.values(savedColors).some(Boolean);

    const [files, setFiles] = useState(emptyFiles);
    const [selectedModel, setSelectedModel] = useState<string | null>(chosenModel);
    const [texts, setTexts] = useState(savedTexts);
    const [colors, setColors] = useState(savedColors);
    const [showAdvanced, setShowAdvanced] = useState(false);

    const resetForm = () => {
        setFiles(emptyFiles);
        setSelectedModel(chosenModel);
        setTexts(savedTexts);
        setColors(savedColors);
    };

    // Al recargar la configuración (después de guardar) el formulario vuelve a lo guardado
    useEffect(resetForm, [configKey]);

    const [busy, setBusy] = useState<string | null>(null);
    const [result, setResult] = useState<WalletSaveResult | null>(null);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [confirmReset, setConfirmReset] = useState<ResetAction | null>(null);

    // Solo se envía lo que cambió; lo que no se manda se conserva
    const changes = useMemo(() => {
        const next: WalletConfigChanges = {};
        (Object.keys(files) as FileKey[]).forEach(key => { if (files[key]) next[key] = files[key]!; });
        if (selectedModel && selectedModel !== chosenModel) next.modelo = selectedModel;
        (Object.keys(texts) as TextKey[]).forEach(key => {
            const value = texts[key].trim();
            // Un texto vacío no borra el guardado (para eso está "Borrar textos")
            if (value && value !== savedTexts[key].trim()) next[TEXT_FIELD[key]] = value;
        });
        (Object.keys(colors) as ColorKey[]).forEach(key => {
            const value = colors[key].trim().toUpperCase();
            if (isHexColor(value) && value !== savedColors[key]) next[COLOR_FIELD[key]] = value;
        });
        return next;
    }, [files, selectedModel, texts, colors, chosenModel, savedTexts, savedColors]);

    const changeCount = Object.keys(changes).length;
    const invalidColors = (Object.keys(colors) as ColorKey[]).filter(key => colors[key].trim() && !isHexColor(colors[key]));
    const clearedTexts = (Object.keys(texts) as TextKey[]).filter(key => !texts[key].trim() && savedTexts[key].trim());
    const textTooLong = (Object.keys(texts) as TextKey[]).some(key => texts[key].length > TEXT_LIMITS[key]);

    const applyResult = async (resp: { result?: WalletSaveResult; error?: string }) => {
        if (resp.error) { setError(resp.error); return; }
        setResult(resp.result ?? null);
        setImagesVersion(v => v + 1);
        await onReload();
    };

    const handleSave = async () => {
        if (changeCount === 0 || invalidColors.length > 0 || textTooLong) return;
        setBusy('save');
        setResult(null);
        const resp = await saveWalletConfig(tipo, changes);
        await applyResult(resp);
        setBusy(null);
    };

    const handleReset = async () => {
        const action = confirmReset;
        setConfirmReset(null);
        if (!action) return;
        setBusy(action);
        setResult(null);
        const resp = await saveWalletConfig(tipo, { [action]: true });
        await applyResult(resp);
        setBusy(null);
    };

    const handleSyncGoogle = async () => {
        setBusy('sync');
        const resp = await syncWalletGoogle(tipo);
        setBusy(null);
        if (resp !== 'success') { setError(resp); return; }
        setResult(prev => prev ? { ...prev, google: { sincronizado: true, mensaje: null } } : prev);
        setInfo('Las tarjetas Android se actualizaron con el diseño guardado.');
    };

    // -------------------- Vista previa --------------------

    const previewApple = async (modelo: string) => {
        setBusy(`apple-${modelo}`);
        const resp = await previewWalletApple(tipo, modelo);
        setBusy(null);
        if (resp.error || !resp.file) { setError(resp.error ?? 'No se pudo generar la vista previa.'); return; }
        downloadBlob(new Blob([resp.file], { type: 'application/vnd.apple.pkpass' }), `tarjeta-${tipo}-${modelo}.pkpass`);
    };

    const previewGoogle = async (modelo: string) => {
        // La pestaña se abre antes de la llamada para que el navegador no la bloquee
        const tab = window.open('', '_blank');
        setBusy(`google-${modelo}`);
        const resp = await previewWalletGoogle(tipo, modelo);
        setBusy(null);
        if (resp.error || !resp.url) {
            tab?.close();
            setError(resp.error ?? 'No se pudo generar la vista previa.');
            return;
        }
        if (tab) tab.location.href = resp.url;
        else window.open(resp.url, '_blank', 'noopener');
    };

    const disabled = busy !== null;
    const setFile = (key: FileKey) => (file: File | null) => setFiles(prev => ({ ...prev, [key]: file }));

    // -------------------- Secciones --------------------

    const textField = (key: TextKey, label: string, placeholder: string, help: string, rows: number, highlight = false) => (
        <div className={highlight ? 'border border-primary-subtle bg-primary-subtle bg-opacity-25 rounded-3 p-3' : ''}>
            <div className="d-flex justify-content-between align-items-end mb-1">
                <label className="form-label small text-muted mb-0" htmlFor={`wallet-${tipo}-${key}`}>
                    {highlight && <i className="bi bi-star-fill text-primary me-1" />}{label}
                </label>
                <span className={`small ${texts[key].length > TEXT_LIMITS[key] ? 'text-danger' : 'text-muted'}`}>
                    {texts[key].length}/{TEXT_LIMITS[key]}
                </span>
            </div>
            {rows > 1 ? (
                <textarea id={`wallet-${tipo}-${key}`} className={`form-control ${texts[key].length > TEXT_LIMITS[key] ? 'is-invalid' : ''}`}
                    rows={rows} maxLength={TEXT_LIMITS[key]} placeholder={placeholder} value={texts[key]} disabled={disabled}
                    onChange={(e) => setTexts(prev => ({ ...prev, [key]: e.target.value }))} />
            ) : (
                <input id={`wallet-${tipo}-${key}`} className={`form-control ${texts[key].length > TEXT_LIMITS[key] ? 'is-invalid' : ''}`}
                    maxLength={TEXT_LIMITS[key]} placeholder={placeholder} value={texts[key]} disabled={disabled}
                    onChange={(e) => setTexts(prev => ({ ...prev, [key]: e.target.value }))} />
            )}
            <div className="form-text">{help}</div>
            {clearedTexts.includes(key) && (
                <div className="form-text text-warning-emphasis">
                    <i className="bi bi-info-circle me-1" />Dejarlo vacío no borra el texto guardado. Use "Borrar textos" para quitarlos.
                </div>
            )}
        </div>
    );

    const premioField = textField('premio', stamps ? 'Premio' : 'Premio (opcional)', '1 café gratis',
        stamps ? 'Se muestra al frente de la tarjeta de sellos: es lo que gana el cliente al completarla.' : 'Máximo 200 caracteres.', 1, stamps);

    return (
        <div>
            {/* -------------------- Estado actual -------------------- */}
            <div className="row g-3 mb-4">
                <div className="col-md-4">
                    <div className="border rounded-3 p-3 h-100">
                        <div className="small text-muted text-uppercase mb-1"><i className="bi bi-apple me-1" />iPhone</div>
                        {config.listoParaUsar
                            ? <div className="text-success fw-semibold"><i className="bi bi-check-circle-fill me-1" />Listo para emitir tarjetas</div>
                            : <div className="text-danger fw-semibold"><i className="bi bi-exclamation-triangle-fill me-1" />No se pueden emitir tarjetas</div>}
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="border rounded-3 p-3 h-100">
                        <div className="small text-muted text-uppercase mb-1"><i className="bi bi-android2 me-1" />Android</div>
                        {config.google?.logoGoogle
                            ? <div className="text-success fw-semibold"><i className="bi bi-check-circle-fill me-1" />Con el logo del comercio</div>
                            : <div className="text-warning-emphasis fw-semibold"><i className="bi bi-info-circle-fill me-1" />Con logo genérico</div>}
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="border rounded-3 p-3 h-100">
                        <div className="small text-muted text-uppercase mb-1"><i className="bi bi-palette me-1" />Diseño actual</div>
                        <div className="fw-semibold">
                            {effective?.modeloNombre ?? effective?.modelo ?? '—'}
                            {!effective?.modeloElegidoPorComercio && <span className="badge bg-light text-muted border ms-2">Predeterminado</span>}
                        </div>
                    </div>
                </div>
            </div>

            {!config.listoParaUsar && (
                <div className="alert alert-danger d-flex gap-2 align-items-start">
                    <i className="bi bi-exclamation-triangle-fill mt-1" />
                    <div>
                        <div className="fw-semibold">Sube tu logo e ícono para poder emitir tarjetas en iPhone</div>
                        <div className="small">Mientras falten, no se podrán crear ni reenviar tarjetas de {PROGRAM_LABEL[tipo].toLowerCase()} a clientes con iPhone.</div>
                    </div>
                </div>
            )}
            {!config.google?.logoGoogle && (
                <div className="alert alert-warning d-flex gap-2 align-items-start">
                    <i className="bi bi-info-circle-fill mt-1" />
                    <div>
                        <div className="fw-semibold">Sube tu logo cuadrado para que tus tarjetas Android muestren tu marca</div>
                        <div className="small">No bloquea nada: mientras tanto se usa un logo genérico.</div>
                    </div>
                </div>
            )}

            {/* -------------------- Resultado del último guardado -------------------- */}
            {result && (
                <div className="mb-4">
                    <div className="alert alert-success d-flex gap-2 align-items-start mb-2">
                        <i className="bi bi-check-circle-fill mt-1" />
                        <div className="flex-grow-1">
                            <div className="fw-semibold">{result.mensaje || 'Configuración guardada.'}</div>
                            {result.faltantes?.length > 0 && (
                                <div className="small">Falta para emitir en iPhone: {result.faltantes.map(f => MISSING_LABEL[f] ?? f).join(', ')}.</div>
                            )}
                        </div>
                        <button type="button" className="btn-close" onClick={() => setResult(null)} />
                    </div>
                    {result.google?.sincronizado === false && (
                        <div className="alert alert-warning d-flex flex-wrap gap-2 align-items-center mb-2">
                            <i className="bi bi-android2" />
                            <div className="flex-grow-1 small">
                                La configuración se guardó, pero no se pudo actualizar la tarjeta en Android.
                                {result.google.mensaje && <> {result.google.mensaje}</>}
                            </div>
                            <button type="button" className="btn btn-sm btn-warning" onClick={handleSyncGoogle} disabled={disabled}>
                                {busy === 'sync' ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-arrow-repeat me-1" />}
                                Reintentar Android
                            </button>
                        </div>
                    )}
                    {result.avisos?.length > 0 && (
                        <div className="alert alert-info mb-0">
                            <div className="small fw-semibold mb-1"><i className="bi bi-lightbulb me-1" />Recomendaciones</div>
                            <ul className="small mb-0 ps-3">
                                {result.avisos.map((aviso, i) => <li key={i}>{aviso}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {/* -------------------- Imágenes -------------------- */}
            <h6 className="fw-semibold mb-1">1. Imágenes del comercio</h6>
            <p className="small text-muted mb-3">Apple y Google usan imágenes distintas. Todas deben ser PNG de máximo 1 MB.</p>
            <div className="row g-3 mb-4">
                <div className="col-lg-4">
                    <WalletImageField label="Logo" platform="iphone" rule={{ kind: 'basic' }}
                        purpose="Aparece arriba a la izquierda de la tarjeta en iPhone. Use su logo horizontal o rectangular."
                        rules="PNG, máx. 1 MB."
                        badge={{ text: 'Obligatorio', className: config.logo ? 'bg-success-subtle text-success-emphasis' : 'bg-danger-subtle text-danger-emphasis' }}
                        savedUrl={savedImages.logo ?? null} savedInfo={config.logo} file={files.logo} onChange={setFile('logo')} disabled={disabled} />
                </div>
                <div className="col-lg-4">
                    <WalletImageField label="Ícono" platform="iphone" rule={{ kind: 'basic' }}
                        purpose="Ícono pequeño que el iPhone muestra en las notificaciones de la tarjeta."
                        rules="PNG, máx. 1 MB."
                        badge={{ text: 'Obligatorio', className: config.icono ? 'bg-success-subtle text-success-emphasis' : 'bg-danger-subtle text-danger-emphasis' }}
                        savedUrl={savedImages.icono ?? null} savedInfo={config.icono} file={files.icono} onChange={setFile('icono')} disabled={disabled} />
                </div>
                <div className="col-lg-4">
                    <WalletImageField label="Logo para Android" platform="android" rule={{ kind: 'square', min: GOOGLE_LOGO_MIN }} circle
                        purpose="Logo de la tarjeta en Google Wallet (Android)."
                        rules={`PNG cuadrado, mínimo ${GOOGLE_LOGO_MIN}×${GOOGLE_LOGO_MIN} px, máx. 1 MB.`}
                        badge={{ text: 'Recomendado', className: config.google?.logoGoogle ? 'bg-success-subtle text-success-emphasis' : 'bg-warning-subtle text-warning-emphasis' }}
                        note={<>
                            <i className="bi bi-circle me-1" />Google lo muestra recortado en círculo: deje margen alrededor del logo.{' '}
                            <a href={TEMPLATE_URL.logoAndroid} download className="text-nowrap"><i className="bi bi-download me-1" />Descargar plantilla logo Android</a>
                        </>}
                        savedUrl={savedImages.logoGoogle ?? null} savedInfo={config.google?.logoGoogle} file={files.logoGoogle} onChange={setFile('logoGoogle')} disabled={disabled} />
                </div>
            </div>

            {/* -------------------- Diseño -------------------- */}
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-1">
                <h6 className="fw-semibold mb-0">2. Diseño de la tarjeta</h6>
                {effective?.modeloElegidoPorComercio && (
                    <button type="button" className="btn btn-sm btn-link p-0" disabled={disabled} onClick={() => setConfirmReset('restablecerModelo')}>
                        <i className="bi bi-arrow-counterclockwise me-1" />Volver al predeterminado
                    </button>
                )}
            </div>
            <p className="small text-muted mb-3">
                Elija un diseño; aplica a iPhone y Android. Las vistas previas usan el logo y los textos <span className="fw-semibold">guardados</span> y no cambian su elección.
            </p>

            {loadingModels ? (
                <div className="text-center py-4"><span className="spinner-border spinner-border-sm me-2" />Cargando diseños...</div>
            ) : modelsError ? (
                <div className="alert alert-danger small">{modelsError}</div>
            ) : (
                <div className="row g-3 mb-4">
                    {models.map(model => {
                        const selected = (selectedModel ?? effective?.modelo) === model.codigo;
                        const inUse = effective?.modelo === model.codigo;
                        return (
                            <div className="col-md-6 col-xl-4" key={model.codigo}>
                                <div
                                    className={`card h-100 ${selected ? 'border-primary border-2 shadow-sm' : ''}`}
                                    style={{ cursor: disabled ? 'default' : 'pointer' }}
                                    onClick={() => !disabled && setSelectedModel(model.codigo)}
                                >
                                    <div className="card-img-top overflow-hidden" style={{ aspectRatio: '1125 / 432', background: model.colorFondo }}>
                                        {banners[model.codigo]
                                            ? <img src={banners[model.codigo]} alt={model.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            : <div className="h-100 d-flex align-items-center justify-content-center"><span className="spinner-border spinner-border-sm" style={{ color: model.colorTexto }} /></div>}
                                    </div>
                                    <div className="card-body d-flex flex-column">
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <input className="form-check-input mt-0" type="radio" name={`wallet-model-${tipo}`} checked={selected} readOnly disabled={disabled} />
                                            <span className="fw-semibold">{model.nombre}</span>
                                            {[model.colorFondo, model.colorTexto, model.colorEtiqueta].map((color, i) => (
                                                <span key={i} className="rounded-circle border" style={{ width: 12, height: 12, background: color }} />
                                            ))}
                                        </div>
                                        <div className="d-flex flex-wrap gap-1 mb-2">
                                            {model.esPropio && <span className="badge bg-info-subtle text-info-emphasis"><i className="bi bi-brush me-1" />Propio</span>}
                                            {model.esPredeterminado && <span className="badge bg-light text-muted border">Predeterminado</span>}
                                            {inUse && <span className="badge bg-success-subtle text-success-emphasis">En uso</span>}
                                            {selected && !inUse && <span className="badge bg-primary-subtle text-primary-emphasis">Seleccionado · sin guardar</span>}
                                        </div>
                                        <p className="small text-muted mb-3">{model.descripcion}</p>
                                        <div className="mt-auto d-flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                                            <button type="button" className="btn btn-sm btn-outline-dark" disabled={disabled || !config.listoParaUsar}
                                                title={config.listoParaUsar ? 'Descarga la tarjeta de prueba (.pkpass) para abrirla en un iPhone' : 'Primero guarde el logo y el ícono de iPhone'}
                                                onClick={() => previewApple(model.codigo)}>
                                                {busy === `apple-${model.codigo}` ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-apple me-1" />}
                                                Ver en iPhone
                                            </button>
                                            <button type="button" className="btn btn-sm btn-outline-success" disabled={disabled}
                                                title="Abre una tarjeta de prueba en Google Wallet (no afecta a sus clientes)"
                                                onClick={() => previewGoogle(model.codigo)}>
                                                {busy === `google-${model.codigo}` ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-android2 me-1" />}
                                                Ver en Android
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <WalletMyDesigns tipo={tipo} baseModels={models.filter(m => !m.esPropio)} canPreviewApple={config.listoParaUsar}
                busy={busy} onPreviewApple={previewApple} onPreviewGoogle={previewGoogle} onChanged={reloadDesigns} />

            {/* -------------------- Textos -------------------- */}
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-1">
                <h6 className="fw-semibold mb-0">3. Textos de la tarjeta</h6>
                {Object.values(savedTexts).some(t => t.trim()) && (
                    <button type="button" className="btn btn-sm btn-link text-danger p-0" disabled={disabled} onClick={() => setConfirmReset('limpiarTextos')}>
                        <i className="bi bi-eraser me-1" />Borrar textos
                    </button>
                )}
            </div>
            <p className="small text-muted mb-3">Se muestran en iPhone y Android. Si un texto queda vacío, no aparece en la tarjeta.</p>
            <div className="row g-3 mb-4">
                {stamps && <div className="col-12">{premioField}</div>}
                <div className="col-12">
                    {textField('contacto', 'Contacto (recomendado)', 'Tel. 2222-3333 · hola@comercio.com',
                        'Teléfono, correo o sitio web del comercio. Apple recomienda mostrarlo en el reverso de la tarjeta.', 1)}
                </div>
                <div className="col-12">
                    {textField('terminos', 'Términos y condiciones', stamps ? 'Los sellos vencen a los 6 meses.' : 'Los puntos vencen a los 12 meses de su acumulación.',
                        'Condiciones del programa que el cliente verá al reverso de la tarjeta.', 4)}
                </div>
                {!stamps && <div className="col-12">{premioField}</div>}
            </div>

            {/* -------------------- Avanzado -------------------- */}
            <button type="button" className="btn btn-sm btn-link text-decoration-none px-0 mb-2" onClick={() => setShowAdvanced(v => !v)}>
                <i className={`bi ${showAdvanced ? 'bi-chevron-down' : 'bi-chevron-right'} me-1`} />Avanzado: colores y banners propios
            </button>
            {showAdvanced && (
                <div className="border rounded-3 p-3 mb-4 bg-light bg-opacity-50">
                    <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-1">
                        <div className="fw-semibold">Colores propios</div>
                        {hasCustomColors && (
                            <button type="button" className="btn btn-sm btn-link p-0" disabled={disabled} onClick={() => setConfirmReset('restablecerColores')}>
                                <i className="bi bi-arrow-counterclockwise me-1" />Usar colores del diseño
                            </button>
                        )}
                    </div>
                    <p className="small text-muted mb-2">Reemplazan los colores del diseño. En Android solo se usa el color de fondo (Google elige el color del texto).</p>
                    <div className="row g-3 mb-4">
                        {([['fondo', 'Fondo', effective?.colorFondo], ['texto', 'Texto', effective?.colorTexto], ['etiqueta', 'Etiquetas', effective?.colorEtiqueta]] as [ColorKey, string, string | null | undefined][])
                            .map(([key, label, designColor]) => {
                                const value = colors[key];
                                const invalid = invalidColors.includes(key);
                                return (
                                    <div className="col-md-4" key={key}>
                                        <label className="form-label small text-muted mb-1">{label}</label>
                                        <div className="input-group input-group-sm has-validation">
                                            <input type="color" className="form-control form-control-color" style={{ maxWidth: 44 }} disabled={disabled}
                                                value={isHexColor(value) ? value : (toHexColor(designColor) || '#000000')}
                                                onChange={(e) => setColors(prev => ({ ...prev, [key]: e.target.value.toUpperCase() }))} />
                                            <input className={`form-control ${invalid ? 'is-invalid' : ''}`} placeholder={toHexColor(designColor) || '#RRGGBB'} maxLength={7} disabled={disabled}
                                                value={value} onChange={(e) => setColors(prev => ({ ...prev, [key]: e.target.value }))} />
                                            <div className="invalid-feedback">Use el formato #RRGGBB.</div>
                                        </div>
                                        {!value && <div className="form-text">Del diseño: {toHexColor(designColor) || '—'}</div>}
                                    </div>
                                );
                            })}
                    </div>

                    <div className="fw-semibold mb-2">Banners propios</div>
                    <div className="row g-3">
                        <div className="col-lg-6">
                            <WalletImageField label="Banner iPhone" platform="iphone" wide rule={{ kind: 'exact', ...STRIP_SIZE }}
                                purpose="Reemplaza el banner del diseño en la tarjeta de iPhone."
                                rules={`PNG de exactamente ${STRIP_SIZE.width}×${STRIP_SIZE.height} px, máx. 1 MB.`}
                                badge={customStrip ? { text: 'Propio', className: 'bg-primary-subtle text-primary-emphasis' } : { text: 'Del diseño', className: 'bg-light text-muted border' }}
                                savedUrl={customStrip ? savedImages.strip ?? null : null} file={files.strip} onChange={setFile('strip')} disabled={disabled} />
                            {customStrip && (
                                <button type="button" className="btn btn-sm btn-link text-danger px-0" disabled={disabled} onClick={() => setConfirmReset('restablecerStrip')}>
                                    Quitar banner propio de iPhone
                                </button>
                            )}
                        </div>
                        <div className="col-lg-6">
                            <WalletImageField label="Banner Android" platform="android" wide rule={{ kind: 'exact', ...HERO_GOOGLE_SIZE }}
                                purpose="Reemplaza el banner del diseño en la tarjeta de Google Wallet."
                                rules={`PNG de exactamente ${HERO_GOOGLE_SIZE.width}×${HERO_GOOGLE_SIZE.height} px, máx. 1 MB.`}
                                badge={customHero ? { text: 'Propio', className: 'bg-primary-subtle text-primary-emphasis' } : { text: 'Del diseño', className: 'bg-light text-muted border' }}
                                savedUrl={customHero ? savedImages.heroGoogle ?? null : null} file={files.heroGoogle} onChange={setFile('heroGoogle')} disabled={disabled} />
                            {customHero && (
                                <button type="button" className="btn btn-sm btn-link text-danger px-0" disabled={disabled} onClick={() => setConfirmReset('restablecerHeroGoogle')}>
                                    Quitar banner propio de Android
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* -------------------- Guardar -------------------- */}
            <div className="sticky-bottom bg-body border-top pt-3 pb-2 d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div className="small text-muted">
                    {changeCount > 0
                        ? <><i className="bi bi-pencil-square me-1 text-primary" />{changeCount} cambio(s) sin guardar en {PROGRAM_LABEL[tipo].toLowerCase()}. Android se actualiza al guardar; iPhone en el siguiente movimiento de cada cliente.</>
                        : 'No hay cambios pendientes.'}
                </div>
                <div className="d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary" disabled={disabled || changeCount === 0} onClick={resetForm}>
                        Descartar
                    </button>
                    <button type="button" className="btn btn-primary" disabled={disabled || changeCount === 0 || invalidColors.length > 0 || textTooLong} onClick={handleSave}>
                        {busy === 'save' && <span className="spinner-border spinner-border-sm me-1" />}
                        Guardar
                    </button>
                </div>
            </div>

            {confirmReset && (
                <Message type="info" title="Confirmar" message={RESET_CONFIRM[confirmReset]} okText="Continuar" okWidth="110px"
                    showCancel handleCancel={() => setConfirmReset(null)} closeDialog={() => setConfirmReset(null)}
                    handleOk={handleReset} backgroundColor="rgba(0,0,0,0.4)" />
            )}
            {error.length > 0 && (
                <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}
            {info.length > 0 && (
                <Message type="success" title="Exitoso" message={info} closeDialog={() => setInfo('')} handleOk={() => setInfo('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}
        </div>
    );
};
